'use client';

import type { FragmentType } from '@packages/gql/generated/shopping';
import type { LoadbeeFragmentFragmentDoc } from '@packages/gql/generated/shopping/LoadbeeFragmentFragmentDoc';
import { DialogHeader } from '@packages/modules/src/Dialog/DialogHeader/DialogHeader.client';
import { useCookieConsent } from '@packages/shared/src/hooks/useCookieConsent/useCookieConsent';
import { Card, CardContent } from '@packages/shared/src/ui/Card/Card';
import { Dialog } from '@packages/shared/src/ui/Dialog/Dialog';
import { DialogContent } from '@packages/shared/src/ui/Dialog/DialogContent/DialogContent';
import { Image } from '@packages/shared/src/ui/Image/Image';
import { useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { unmask } from '@packages/gql/src/betterMasking';
import { useManufacturerTracking } from '../Tracking/hooks/useManufacturerTracking/useManufacturerTracking';

/* GraphQL */ `
  fragment LoadbeeFragment on Product {
    brand {
      image
      name
    }
    # is needed for loadbee
    ean
    loadbee(locale: $locale) {
      url
    }
  }
`;

export type LoadbeeCardProps = {
  data: FragmentType<typeof LoadbeeFragmentFragmentDoc>;
};

export const LoadbeeCard = ({ data }: LoadbeeCardProps) => {
  const { brand, loadbee } = unmask<typeof LoadbeeFragmentFragmentDoc>(data);
  const isAllowed = useCookieConsent('C0004');
  const { formatMessage } = useIntl();
  const cardRef = useRef<HTMLDivElement>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const { trackGlycerinButtonClick, trackGlycerinDisplayOverlay } = useManufacturerTracking({
    brandName: brand?.name || '',
    ref: cardRef,
    vendor: 'Loadbee',
  });

  const onClickHandler = () => {
    setIsDialogOpen(true);
    trackGlycerinButtonClick();
    trackGlycerinDisplayOverlay();
  };

  if (!loadbee || !isAllowed) {
    return null;
  }

  return (
    <div>
      <Card ref={cardRef}>
        <CardContent
          className="flex cursor-pointer items-center gap-1.5 p-1.5"
          onClick={onClickHandler}
        >
          {brand?.image && (
            <Image
              loading="lazy"
              src={brand.image}
              alt={brand.name}
              style={{ objectFit: 'contain' }}
              width={50}
              height={30}
            />
          )}
          <span>
            {formatMessage(
              {
                defaultMessage: 'Weitere Herstellerinformationen von {brand}',
                id: 'loadbee.label.text',
              },
              {
                brand: brand?.name || '',
              },
            )}
          </span>
        </CardContent>
      </Card>
      <Dialog
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        className="h-full w-full md:h-[65%]"
      >
        <DialogHeader onClose={() => setIsDialogOpen(false)}>
          {formatMessage(
            {
              defaultMessage: 'Weitere Herstellerinformationen von {brand}',
              id: 'loadbee.label.text',
            },
            {
              brand: brand?.name || '',
            },
          )}
        </DialogHeader>
        <DialogContent className="px-1">
          <iframe title="title" src={loadbee.url} className="h-full w-full" />
        </DialogContent>
      </Dialog>
    </div>
  );
};
